import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DocumentArrowDownIcon,
  CheckCircleIcon,
  PlayIcon,
  StopIcon,
  DocumentTextIcon,
  BellIcon,
  CogIcon,
  CheckIcon,
  RectangleStackIcon,
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ArchiveBoxIcon,
  PresentationChartLineIcon,
  CubeIcon,
  FolderIcon,
  BookmarkIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { NodeType } from "../../types";
import { getNodeDisplayName } from "../../utils/workflowUtils";
import { ActivityLibrary } from "../activities/ActivityLibrary";

interface WorkflowToolbarProps {
  onSave: () => void;
  canSave: boolean;
  onAddActivityFromPool?: (activity: any) => void;
  onAddTemplateFromPool?: (templateId: number) => void;
  onSaveAsTemplate?: () => void;
  onLoadTemplate?: () => void;
}

export const WorkflowToolbar: React.FC<WorkflowToolbarProps> = ({
  onSave,
  canSave,
  onAddActivityFromPool,
  onSaveAsTemplate,
  onLoadTemplate,
}) => {
  const { t } = useTranslation(["workflows"]);
  const [showActivityPool, setShowActivityPool] = useState(false);

  const onDragStart = (event: React.DragEvent, nodeType: NodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleActivityFromPoolSelect = (activity: any) => {
    onAddActivityFromPool?.(activity);
    setShowActivityPool(false);
  };

  const nodeTypeConfigs = [
    {
      type: NodeType.START,
      label: t("workflows:startNode"),
      icon: PlayIcon,
      description: t("workflows:startNodeDescription"),
      category: "basic",
    },
    {
      type: NodeType.ACTIVITY,
      label: t("workflows:newActivity"),
      icon: DocumentTextIcon,
      description: t("workflows:newActivityDescription"),
      category: "basic",
    },
    {
      type: NodeType.END,
      label: t("workflows:endNode"),
      icon: StopIcon,
      description: t("workflows:endNodeDescription"),
      category: "basic",
    },
  ];

  // Artık tüm node'lar her workflow tipi için kullanılabilir
  const availableNodes = nodeTypeConfigs;

  return (
    <>
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <h3 className="text-lg font-medium text-gray-900">
              {t("workflows:workflowComponents")}
            </h3>

            {/* Basic Workflow Components */}
            <div className="flex items-center space-x-2">
              {availableNodes.map((config) => {
                const IconComponent = config.icon;
                return (
                  <div
                    key={config.type}
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg cursor-grab hover:bg-gray-100 transition-colors"
                    draggable
                    onDragStart={(event) => onDragStart(event, config.type)}
                    title={config.description}
                  >
                    <IconComponent className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {config.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Activity Pool Button */}
            <div className="border-l border-gray-300 pl-4">
              <button
                onClick={() => setShowActivityPool(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors text-blue-700"
                title={t("workflows:selectFromActivityPool")}
              >
                <FolderIcon className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t("workflows:activityPool")}
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {onLoadTemplate && (
              <button
                onClick={onLoadTemplate}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors"
                title={t("workflows:loadTemplate")}
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
                <span>{t("workflows:loadTemplate")}</span>
              </button>
            )}

            {onSaveAsTemplate && (
              <button
                onClick={onSaveAsTemplate}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                title={t("workflows:saveAsTemplate")}
              >
                <BookmarkIcon className="w-4 h-4" />
                <span>{t("workflows:saveAsTemplate")}</span>
              </button>
            )}

            {canSave && (
              <button
                onClick={onSave}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4" />
                <span>{t("workflows:saveWorkflow")}</span>
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 text-sm text-gray-500">
          {t("workflows:dragAndDropHint", {
            newActivity: t("workflows:newActivity"),
            activityPool: t("workflows:activityPool"),
          })}
        </div>
      </div>

      {/* Activity Pool Modal */}
      {showActivityPool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl h-5/6 mx-4 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("workflows:activityPoolAddToWorkflow")}
              </h3>
              <button
                onClick={() => setShowActivityPool(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <ActivityLibrary
                selectionMode={true}
                onSelectActivity={handleActivityFromPoolSelect}
                multiSelect={false}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
